"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X, ArrowRight, Clock, Target, Users, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Clarity() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"all" | "ideal" | "not-ideal">("all");
  const idealCriteria = [{
    title: "Considered, Multi-Stakeholder Buying Decisions",
    description: "Businesses with evaluation processes, technical or commercial review stages, and multi-touch buyer journeys.",
    icon: Clock
  }, {
    title: "Solution-Based B2B Services",
    description: "High-value B2B platforms, SaaS, professional consultancies, and specialised industrial solutions.",
    icon: Target
  }, {
    title: "Commercial Intent & Decision-Makers",
    description: "Targeting senior decision-makers and buying committees searching with high commercial intent, rather than broad vanity traffic.",
    icon: Users
  }, {
    title: "High Customer Lifetime Value",
    description: "Organisations where securing high-intent commercial search visibility delivers lasting pipeline contribution.",
    icon: ShieldCheck
  }];
  const notIdealCriteria = [{
    title: "Transactional / Flash Sales",
    description: "Impulse-buy e-commerce or low-margin retail products requiring immediate overnight demand creation.",
    icon: X
  }, {
    title: "Ultra-Short Sales Cycles",
    description: "Businesses expecting instant, pay-per-click style volume spikes without organic authority building.",
    icon: X
  }, {
    title: "Cheap Bulk Link Building",
    description: "Companies seeking mass spam backlinks or quick automated SEO tactics rather than real strategy.",
    icon: X
  }, {
    title: "Work That Needs a Large Delivery Team",
    description: "Organisations looking for large agency squads or multi-tier bureaucracy rather than direct senior consultant delivery.",
    icon: X
  }];
  return <section id="clarityfit" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Clear Fit & Transparency
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Is my B2B SEO consultancy right for your business?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            I believe in absolute clarity before you commit. Grounded in a strong{" "}
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              technical SEO foundation
            </Link>
            , I work directly with clients where my strategic SEO framework drives sustainable, high-value organic visibility and lead quality.
          </p>
        </div>

        <div className="flex justify-center items-center gap-2 mb-10">
          <Button variant={activeTab === "all" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("all")} className="rounded-full px-5">
            All Criteria
          </Button>
          <Button variant={activeTab === "ideal" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("ideal")} className="rounded-full px-5">
            Best Fit Only
          </Button>
          <Button variant={activeTab === "not-ideal" ? "default" : "outline"} size="sm" onClick={() => setActiveTab("not-ideal")} className="rounded-full px-5">
            Not Ideal
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {(activeTab === "all" || activeTab === "ideal") && <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-border">
                <div className="size-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Check className="size-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold font-default text-foreground">Who I Work Best With</h3>
                  <p className="text-sm text-muted-foreground">High impact, complex B2B & specialised search environments</p>
                </div>
              </div>

              <div className="grid gap-4">
                {idealCriteria.map((item, i) => {
              const Icon = item.icon;
              return <Card data-index={i} key={`ideal-${i}`} className="border border-border/80 bg-card hover:border-primary/40 transition-all shadow-xs">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2.5">
                          <Icon className="size-4 text-primary shrink-0" />
                          <span>{item.title}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </CardContent>
                    </Card>;
            })}
              </div>
            </motion.div>}

          {(activeTab === "all" || activeTab === "not-ideal") && <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-border">
                <div className="size-8 rounded-full bg-secondary flex items-center justify-center text-muted-foreground">
                  <X className="size-5" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold font-default text-foreground">When I Am Not the Right Fit</h3>
                  <p className="text-sm text-muted-foreground">To save your time and budget, I am direct about limitations</p>
                </div>
              </div>

              <div className="grid gap-4">
                {notIdealCriteria.map((item, i) => {
              return <Card data-index={i} key={`not-ideal-${i}`} className="border border-border bg-muted/40 transition-all shadow-xs">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold text-foreground/80 flex items-center gap-2.5">
                          <X className="size-4 text-muted-foreground shrink-0" />
                          <span>{item.title}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </CardContent>
                    </Card>;
            })}
              </div>
            </motion.div>}
        </div>

        <div className="mt-16 bg-card border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-sm">
          <div className="inline-flex items-center justify-center size-12 rounded-full bg-primary/10 text-primary mb-4">
            <ShieldCheck className="size-6" />
          </div>
          <h3 className="text-2xl font-semibold font-default text-foreground">
            Does your organisation fit my focus?
          </h3>
          <p className="mt-2 text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            If you manage a long sales cycle or solution-based service, whether starting with a comprehensive{" "}
            <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              SEO audit
            </Link>{" "}
            or building a sustainable organic search pipeline to drive qualified leads, let&apos;s talk.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto rounded-full font-medium">
              <Link to="/CaseStudiesPage">Explore My Case Studies</Link>
            </Button>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-border/60 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
            Explore Related SEO Services
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/NationalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              National SEO strategy
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/InternationalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              International SEO support
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Technical SEO expertise
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/AISearchOptimisationPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              AI Search Optimisation
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/B2BProfessionalServicesPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              B2B & Professional Services
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/MonthlySeoServicesPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Monthly SEO Services
            </Link>
            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>
            <Link to="/HireSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
              Hire an SEO consultant
            </Link>
          </div>
        </div>
      </div>
    </section>;
}